import type {BoardConfiguration} from '@/types/monday/boardConfiguration';

import {getCollection} from '@/services/FirestoreService';
import {wrapError} from '@northpoint-development/labs-common-core';

enum BoardNames {
  BROWNFIELD_REQUIREMENTS = 'Brownfield Site Requirements',
  BONDS_LETTER_CREDIT = 'Bonds and Letter of Credit',
}

const configsByBoardId = {} as {[boardId: string]: BoardConfiguration};
const boardNamesToIds = {} as {[boardName in BoardNames]: string};

async function getBoardConfigurations() {
  const collection = await getCollection('beyondMaps-monday-board-configs');
  collection.docs.forEach((doc) => {
    const data = doc.data() as BoardConfiguration;
    configsByBoardId[doc.id] = data;
    boardNamesToIds[data.boardName as BoardNames] = doc.id;
  });

  if (!configsByBoardId || !Object.keys(configsByBoardId).length) {
    throw wrapError(new Error('No board configurations found'), 'Could not get board configuration');
  }
}

/**
 * Returns the configuration for a specific Monday.com board, identified by its name.
 * @param name The name of the board whose configuration to retrieve.
 */
async function getBoardConfigurationByName(name: BoardNames) {
  if (!Object.keys(configsByBoardId).length) {
    await getBoardConfigurations();
  }

  const targetConfig = Object.values(configsByBoardId).find((config) => config.boardName === name);
  if (!targetConfig) {
    throw wrapError(new Error(`No board configuration found for board name: ${name}`), 'Could not get board configuration');
  }

  return targetConfig;
}

/**
 * Returns the ID for a specific Monday.com board, identified by its name.
 * @param name The name of the board whose ID to retrieve.
 */
async function getBoardIdByName(name: BoardNames) {
  if (!Object.keys(boardNamesToIds).length) {
    await getBoardConfigurations();
  }

  const targetId = boardNamesToIds[name];
  if (!targetId) {
    throw wrapError(new Error(`No board ID found for board name: ${name}`), 'Could not get board configuration');
  }

  return targetId;
}

export {
  getBoardConfigurationByName,
  getBoardIdByName,

  BoardNames,
};
